import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  Alert as MuiAlert,
  CircularProgress,
  Button,
  Stack,
  TextField,
  Grid,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import WarningIcon from '@mui/icons-material/Warning';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/ApiService';

interface Alert {
  id: number;
  boitier_id: number;
  boitier_nom: string;
  constante: string;
  valeur: string;
  message: string;
  etat: string;
  date_apparition: string;
  date_fin: string | null;
  min_threshold: number | null;
  max_threshold: number | null;
  unit?: string;
  field_name?: string;
}

const AlertsView: React.FC = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterActive, setFilterActive] = useState<boolean | null>(true);
  
  // États pour les filtres de dates
  const [startDate, setStartDate] = useState<Date | null>(new Date(Date.now() - 24 * 60 * 60 * 1000)); // Par défaut: 24h avant
  const [endDate, setEndDate] = useState<Date | null>(new Date()); // Par défaut: maintenant
  const [dateFilterEnabled, setDateFilterEnabled] = useState<boolean>(false);

  // Charger les alertes
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Déterminer l'état à filtrer
        let etatFilter: string | null = null;
        if (filterActive === true) {
          etatFilter = 'active';
        } else if (filterActive === false) {
          etatFilter = 'résolue';
        }
        
        // Préparer les dates si le filtre est activé
        let dateDebut: string | undefined;
        let dateFin: string | undefined;
        if (dateFilterEnabled && startDate && endDate) {
          dateDebut = startDate.toISOString();
          dateFin = endDate.toISOString();
        }
        
        // Appeler l'API avec tous les paramètres
        const data = await apiService.getAlerts(
          etatFilter,
          dateDebut,
          dateFin
        );
        
        setAlerts(data);
      } catch (err: any) {
        console.error('Erreur lors de la récupération des alertes:', err);
        setError(`Impossible de charger les alertes : ${err.message || 'Erreur de connexion'}`);
        setAlerts([]); // Réinitialiser les alertes en cas d'erreur
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
    
    // Actualisation automatique seulement si pas de filtre de date actif
    let intervalId: NodeJS.Timeout | undefined;
    if (!dateFilterEnabled) {
      intervalId = setInterval(fetchAlerts, 30000);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [filterActive, dateFilterEnabled, startDate, endDate]);

  // Gérer le changement de page
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Gérer le changement de lignes par page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Naviguer vers la page de détails du boîtier
  const handleViewBain = (boitierId: number) => {
    navigate(`/boitier/${boitierId}`);
  };

  // Formater la valeur du paramètre avec l'unité (dynamique)
  const formatParameterValue = (alert: Alert) => {
    if (!alert.valeur) return '-';
    
    const value = parseFloat(alert.valeur);
    if (isNaN(value)) return alert.valeur;
    
    // Utiliser l'unité de l'alerte si disponible, sinon fallback sur les valeurs classiques
    const unit = alert.unit;
    if (unit) {
      return `${value.toFixed(2)} ${unit}`;
    }
    
    // Fallback pour les champs classiques
    switch (alert.constante?.toLowerCase()) {
      case 'temp':
        return `${value.toFixed(1)} °C`;
      case 'ph':
        return value.toFixed(2);
      case 'cond':
        return `${value.toFixed(2)} mS/cm`;
      default:
        return value.toFixed(2);
    }
  };

  // Calculer la sévérité de l'alerte (à quel point la valeur est hors limites)
  const getAlertSeverity = (alert: Alert) => {
    if (!alert.min_threshold || !alert.max_threshold) return 'warning';
    
    const value = parseFloat(alert.valeur);
    
    if (value < alert.min_threshold) {
      const deviation = (alert.min_threshold - value) / alert.min_threshold * 100;
      return deviation > 10 ? 'error' : 'warning';
    }
    if (value > alert.max_threshold) {
      const deviation = (value - alert.max_threshold) / alert.max_threshold * 100;
      return deviation > 10 ? 'error' : 'warning';
    }
    return 'success';
  };
  
  // Formater l'affichage des seuils (dynamique)
  const formatThresholds = (alert: Alert) => {
    if (alert.min_threshold === null || alert.max_threshold === null) return '-';
    
    // Utiliser l'unité de l'alerte si disponible
    const unit = alert.unit;
    if (unit) {
      return `${alert.min_threshold.toFixed(2)} ${unit} - ${alert.max_threshold.toFixed(2)} ${unit}`;
    }
    
    // Fallback pour les champs classiques
    switch(alert.constante.toLowerCase()) {
      case 'temp':
        return `${alert.min_threshold.toFixed(1)} °C - ${alert.max_threshold.toFixed(1)} °C`;
      case 'ph':
        return `${alert.min_threshold.toFixed(2)} - ${alert.max_threshold.toFixed(2)}`;
      case 'cond':
        return `${alert.min_threshold.toFixed(2)} mS/cm - ${alert.max_threshold.toFixed(2)} mS/cm`;
      default:
        return `${alert.min_threshold.toFixed(2)} - ${alert.max_threshold.toFixed(2)}`;
    }
  };
  
  // Formater le nom du paramètre (dynamique)
  const formatParameter = (constante: string, alert?: Alert) => {
    // Utiliser le field_name de l'alerte si disponible
    const fieldName = alert?.field_name;
    if (fieldName) return fieldName;
    
    // Fallback pour les champs classiques
    switch(constante.toLowerCase()) {
      case 'temp': return 'Température';
      case 'ph': return 'pH';
      case 'cond': return 'Conductivité';
      default: return constante;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Journal des alertes
      </Typography>
      
      
      <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
        {/* Filtres par état */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Button 
            variant={filterActive === null ? "contained" : "outlined"} 
            onClick={() => setFilterActive(null)}
          >
            Toutes
          </Button>
          <Button 
            variant={filterActive === true ? "contained" : "outlined"} 
            color="error"
            onClick={() => setFilterActive(true)}
          >
            Actives
          </Button>
          <Button 
            variant={filterActive === false ? "contained" : "outlined"} 
            color="success"
            onClick={() => setFilterActive(false)}
          >
            Résolues
          </Button>
        </Stack>
        
        {/* Filtres de date */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom>
            Fenêtre temporelle
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={2}>
              <Button
                variant={dateFilterEnabled ? "contained" : "outlined"}
                color="primary"
                onClick={() => setDateFilterEnabled(!dateFilterEnabled)}
                fullWidth
              >
                {dateFilterEnabled ? "Filtre actif" : "Activer filtre"}
              </Button>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <Box sx={{ width: '100%' }}>
                  <DateTimePicker
                    label="De"
                    value={startDate}
                    onChange={(value) => setStartDate(value as Date | null)}
                    disabled={!dateFilterEnabled}
                    renderInput={(params) => (
                      <TextField 
                        {...params}
                        fullWidth
                        error={!!(startDate && endDate && startDate > endDate)}
                        helperText={startDate && endDate && startDate > endDate ? "La date de début doit être antérieure à la date de fin" : ""}
                      />
                    )}
                  />
                </Box>
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <Box sx={{ width: '100%' }}>
                  <DateTimePicker
                    label="À"
                    value={endDate}
                    onChange={(value) => setEndDate(value as Date | null)}
                    disabled={!dateFilterEnabled}
                    minDateTime={startDate || undefined}
                    renderInput={(params) => (
                      <TextField 
                        {...params}
                        fullWidth
                        error={!!(startDate && endDate && startDate > endDate)}
                        helperText={startDate && endDate && startDate > endDate ? "La date de fin doit être postérieure à la date de début" : ""}
                      />
                    )}
                  />
                </Box>
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setStartDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
                  setEndDate(new Date());
                }}
                disabled={!dateFilterEnabled}
                fullWidth
              >
                Dernier jour
              </Button>
            </Grid>
          </Grid>
        </Paper>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        ) : alerts.length > 0 ? (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="table des alertes">
                <TableHead>
                  <TableRow>
                    <TableCell>Boîtier</TableCell>
                    <TableCell>État</TableCell>
                    <TableCell>Paramètre</TableCell>
                    <TableCell>Valeur</TableCell>
                    <TableCell>Seuils</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alerts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((alert) => (
                      <TableRow
                        key={alert.id}
                        sx={{ bgcolor: alert.etat === 'active' ? undefined : '#f8fbf8' }}
                      >
                        <TableCell component="th" scope="row">
                          {alert.boitier_nom}
                        </TableCell>
                        <TableCell>
                          {alert.etat === 'active' ? (
                            <Chip 
                              icon={<WarningIcon />} 
                              label="Active" 
                              size="small" 
                              color="error" 
                            />
                          ) : (
                            <Chip 
                              label="Résolue" 
                              size="small" 
                              color="success" 
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatParameter(alert.constante, alert)}</TableCell>
                        <TableCell>
                          <Typography color={alert.etat === 'active' ? 'error.main' : 'text.primary'}>
                            {formatParameterValue(alert)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatThresholds(alert)}
                        </TableCell>
                        <TableCell>
                          {format(new Date(alert.date_apparition), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}
                        </TableCell>
                        <TableCell align="center">
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => handleViewBain(alert.boitier_id)}
                          >
                            Détails
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={alerts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
            />
          </>
        ) : (
          <MuiAlert severity="info" sx={{ mb: 2 }}>
            {filterActive === true 
              ? "Aucune alerte active. Tous les paramètres sont dans les seuils acceptables."
              : filterActive === false
              ? "Aucune alerte résolue à afficher."
              : "Aucune alerte à afficher."}
          </MuiAlert>
        )}
      </Paper>
    </Box>
  );
};

export default AlertsView;
